import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { css } from '@emotion/react';
import { gsap, ScrollTrigger } from 'gsap/all';
import { fullHeight, useHsHeader } from '../../util/hsHeader';

import { useDeviceSize } from '../../util/useDeviceSize';

import { AnimatedTextWrapper } from '../../styles/helpers';
import { H3 } from '../../styles/typography';
import VideoMobile from '../VideoMobile';
import FullScreenVideo from '../FullScreenVideo';

gsap.registerPlugin(ScrollTrigger);

export default function VideoSlide4({ images, copy, video }) {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [{ headerHeight }] = useHsHeader();
  const { isWideScreen } = useDeviceSize();

  const slideTriggerRef = useRef();
  const videoRef = useRef();
  const copyRef = useRef();
  const imageRef = useRef();
  const imageHolderRef = useRef();

  useEffect(() => {
    if (!isWideScreen) {
      return undefined;
    }

    const tl = gsap
      .timeline()
      .to(slideTriggerRef.current, {
        x: 0,
        duration: 0.2,
        ease: 'ease-in',
      })
      .to(imageHolderRef.current, {
        x: 0,
        duration: 0.2,
        ease: 'ease-in',
      })
      .to(imageRef.current, {
        x: '-100%',
        duration: 0.4,
        delay: -0.2,
        ease: 'ease-in',
      })
      .to(videoRef.current, {
        opacity: 1,
        duration: 0.2,
        ease: 'ease-in',
      })
      .to(copyRef.current, {
        opacity: 0,
        duration: 0.2,
        delay: -0.2,
        ease: 'ease-in',
      })
      .add(() => {
        setIsVideoPlaying(v => !v);
      })
      .to(slideTriggerRef.current, {
        duration: 0.4,
      })
      .add(() => {
        setIsVideoPlaying(v => !v);
      })
      .to(slideTriggerRef.current, {
        duration: 0.2,
      });

    const st = ScrollTrigger.create({
      animation: tl,
      trigger: slideTriggerRef.current,
      pin: true,
      pinSpacing: false,
      scrub: true,
      start: 'top top',
      end: `bottom center`,
      anticipatePin: 1,
    });

    return () => {
      tl.kill();
      st.kill();
    };
  }, [headerHeight, isWideScreen]);

  if (isWideScreen) {
    return (
      <div
        css={css`
          ${fullHeight(6)};
          position: relative;
          z-index: 2;
          display: flex;
          transform: translateX(100%) translateY(-50vh);
        `}
        ref={slideTriggerRef}
      >
        <div
          ref={videoRef}
          css={css`
            opacity: 0;
            width: 100%;
          `}
        >
          <FullScreenVideo videoId={video.id} pause={!isVideoPlaying} />
        </div>

        <div
          css={css`
            ${fullHeight()};
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            pointer-events: none;
          `}
        >
          <div
            css={css`
              position: absolute;
              top: 0;
              width: 100%;
              height: 100%;
              transform: translateX(100%);
            `}
            ref={imageHolderRef}
          >
            <img
              css={css`
                height: 100%;
              `}
              src={images[0].src}
              alt=""
              ref={imageRef}
            />
          </div>
          <H3
            css={css`
              position: relative;
              margin-top: 10vh;
            `}
            ref={copyRef}
          >
            {copy[0].text}
          </H3>
        </div>
      </div>
    );
  }

  return (
    <div>
      <AnimatedTextWrapper>
        <H3 mobile>{copy[0].text}</H3>
      </AnimatedTextWrapper>

      <VideoMobile videoId={video.id} />
    </div>
  );
}

VideoSlide4.propTypes = {
  video: PropTypes.shape({ id: PropTypes.number }),
  images: PropTypes.arrayOf(PropTypes.shape({ src: PropTypes.string })),
  copy: PropTypes.arrayOf(PropTypes.shape({ text: PropTypes.node })),
};

VideoSlide4.defaultProps = {
  video: PropTypes.shape({ id: PropTypes.number }),
  images: null,
  copy: null,
};
